/**
 * Cache Service - Centralized management of client-side data caching
 * 
 * FIXED (v2):
 * ✅ Proper TTL enforcement (expires cached data)
 * ✅ Cache invalidation support (invalidate related entries)
 * ✅ Category-based cache clearing (clear roadmaps, articles, etc separately)
 * ✅ Cache statistics for debugging
 * ✅ Prevents stale data being used indefinitely
 */

export interface CacheEntry<T> {
  data: T;
  expiresAt: number;  // Fixed: renamed from 'timestamp' to be clearer
  category: string;   // NEW: for grouped invalidation
  version: number;    // NEW: for cache busting on schema changes
  savedAt: number;    // NEW: timestamp when saved (for 7-day clearing)
}

export type CacheCategory = 'roadmap' | 'article' | 'video' | 'ai' | 'subscription' | 'profile' | 'user' | 'progress';

export class CacheService {
  private static readonly STORAGE_PREFIX = 'nanolez_v2_';
  private static readonly VERSION = 2;  // Increment on schema changes
  private static readonly SEVEN_DAYS_MS = 1000 * 60 * 60 * 24 * 7;  // 7 days in milliseconds
  private static readonly CACHE_CLEAR_CHECK_KEY = 'nanolez_cache_last_clear';
  
  // Updated TTLs - shorter defaults to prevent stale data
  private static readonly TTL_DEFAULTS: Record<CacheCategory, number> = {
    roadmap: 1000 * 60 * 60 * 24 * 7,      // 7 days - content is stable
    article: 1000 * 60 * 60 * 24 * 7,      // 7 days - content is stable
    video: 1000 * 60 * 60 * 24 * 30,       // 30 days - video links rarely change
    ai: 1000 * 60 * 60 * 24 * 7,           // 7 days - AI responses are stable
    subscription: 1000 * 60 * 5,            // 5 minutes - refresh status frequently
    profile: 1000 * 60 * 60,                // 1 hour - user profile changes rarely
    user: 1000 * 60 * 60 * 24 * 7,         // 7 days - user data is stable
    progress: 1000 * 60 * 60 * 24 * 7,     // 7 days - progress data is stable
  };

  /**
   * Initialize cache service - should be called on app startup
   * Clears all cache entries older than 7 days
   */
  static initialize(): void {
    console.log('🚀 Initializing Cache Service...');
    const lastClearTime = localStorage.getItem(this.CACHE_CLEAR_CHECK_KEY);
    const now = Date.now();
    
    // Check if we should clear old cache (do it once every day)
    if (!lastClearTime || now - parseInt(lastClearTime) > 1000 * 60 * 60 * 24) {
      this.clearEntriesOlderThan7Days();
      localStorage.setItem(this.CACHE_CLEAR_CHECK_KEY, now.toString());
    }
    
    const stats = this.getStats();
    console.log(`📊 Cache Stats:`, stats);
  }

  /**
   * Clear all cache entries older than 7 days
   */
  private static clearEntriesOlderThan7Days(): void {
    const now = Date.now();
    const sevenDaysAgo = now - this.SEVEN_DAYS_MS;
    let clearedCount = 0;
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX) && key !== this.CACHE_CLEAR_CHECK_KEY) {
        try {
          const raw = localStorage.getItem(key) || '';
          const entry = JSON.parse(raw) as CacheEntry<unknown>;
          
          // Clear if saved more than 7 days ago
          if (entry.savedAt && entry.savedAt < sevenDaysAgo) {
            localStorage.removeItem(key);
            clearedCount++;
            console.debug(`🗑️ Cleared old cache (7d+): ${key}`);
          }
        } catch {
          // Remove corrupted entries
          localStorage.removeItem(key);
          clearedCount++;
        }
      }
    });
    
    if (clearedCount > 0) {
      console.info(`✅ Cleared ${clearedCount} cache entries older than 7 days`);
    }
  }

  /**
   * Save data to local cache with TTL and category
   */
  static set<T>(
    key: string,
    data: T,
    ttlMs?: number,
    category: CacheCategory = 'profile'
  ): void {
    try {
      const expiresAt = Date.now() + (ttlMs ?? this.TTL_DEFAULTS[category]);
      
      const entry: CacheEntry<T> = {
        data,
        expiresAt,
        category,
        version: this.VERSION,
        savedAt: Date.now(),  // Save current timestamp for 7-day clearing
      };
      
      localStorage.setItem(
        this.STORAGE_PREFIX + key,
        JSON.stringify(entry)
      );
      
      console.debug(`💾 Cached [${category}]: ${key} (expires in ${Math.round((expiresAt - Date.now()) / 1000)}s)`);
    } catch (error) {
      console.warn('Cache write failed:', error);
      // If storage is full, clear old cached items
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clearOldEntries();
        // Retry once
        try {
          const entry: CacheEntry<T> = {
            data,
            expiresAt: Date.now() + (ttlMs ?? this.TTL_DEFAULTS[category]),
            category,
            version: this.VERSION,
            savedAt: Date.now(),
          };
          localStorage.setItem(this.STORAGE_PREFIX + key, JSON.stringify(entry));
        } catch (e) {
          console.error('Cache write failed after cleanup:', e);
        }
      }
    }
  }

  /**
   * Retrieve data from local cache
   * Returns null if expired or corrupted
   */
  static get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_PREFIX + key);
      if (!raw) {
        console.debug(`⚠️ Cache miss: ${key}`);
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(raw);
      
      // Check version compatibility
      if (entry.version !== this.VERSION) {
        console.debug(`♻️ Cache invalidated [version]: ${key}`);
        localStorage.removeItem(this.STORAGE_PREFIX + key);
        return null;
      }
      
      // Check if expired
      if (Date.now() > entry.expiresAt) {
        console.debug(`⏰ Cache expired: ${key}`);
        localStorage.removeItem(this.STORAGE_PREFIX + key);
        return null;
      }

      console.debug(`✅ Cache hit: ${key}`);
      return entry.data;
    } catch (error) {
      console.warn(`Cache read error for ${key}:`, error);
      // Remove corrupted entry
      localStorage.removeItem(this.STORAGE_PREFIX + key);
      return null;
    }
  }

  /**
   * Remove a specific cache entry
   */
  static remove(key: string): void {
    localStorage.removeItem(this.STORAGE_PREFIX + key);
    console.debug(`🗑️ Cache removed: ${key}`);
  }

  /**
   * Invalidate all cache entries of a specific category
   * Useful when data changes (e.g., user upgrades subscription)
   */
  static invalidateCategory(category: CacheCategory): void {
    let invalidatedCount = 0;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const entry = JSON.parse(raw) as CacheEntry<unknown>;
            if (entry.category === category) {
              localStorage.removeItem(key);
              invalidatedCount++;
            }
          }
        } catch {
          // Ignore parse errors
        }
      }
    });
    console.info(`Invalidated ${invalidatedCount} ${category} cache entries`);
  }

  /**
   * Invalidate multiple categories at once
   */
  static invalidateCategories(...categories: CacheCategory[]): void {
    categories.forEach(cat => this.invalidateCategory(cat));
  }

  /**
   * Clear all cache entries managed by this service
   */
  static clearAll(): void {
    let clearedCount = 0;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    });
    console.info(`Cleared all cache (${clearedCount} entries)`);
  }

  /**
   * Get cache statistics for debugging
   */
  static getStats(): {
    totalEntries: number;
    byCategory: Record<CacheCategory, number>;
    totalSize: number;
    expiredCount: number;
  } {
    let totalEntries = 0;
    let expiredCount = 0;
    let totalSize = 0;
    const byCategory: Record<CacheCategory, number> = {
      roadmap: 0,
      article: 0,
      video: 0,
      ai: 0,
      subscription: 0,
      profile: 0,
      user: 0,
      progress: 0,
    };

    const now = Date.now();
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const raw = localStorage.getItem(key) || '';
          const entry = JSON.parse(raw) as CacheEntry<unknown>;
          totalEntries++;
          totalSize += raw.length;
          
          if (entry.category in byCategory) {
            byCategory[entry.category as CacheCategory]++;
          }
          
          if (now > entry.expiresAt) {
            expiredCount++;
          }
        } catch {
          // Ignore parse errors
        }
      }
    });

    return { totalEntries, byCategory, totalSize, expiredCount };
  }

  /**
   * Generate a standardized cache key
   */
  static generateKey(...parts: (string | number | boolean)[]): string {
    return parts
      .map(p => String(p).toLowerCase().trim())
      .join('_')
      .replace(/[^a-z0-9_]/g, '-')
      .substring(0, 200);
  }

  /**
   * Internal: Clear expired entries to free up storage space
   */
  private static clearOldEntries(): void {
    const now = Date.now();
    let clearedCount = 0;
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const raw = localStorage.getItem(key) || '';
          const entry = JSON.parse(raw) as CacheEntry<unknown>;
          
          if (entry.expiresAt < now) {
            localStorage.removeItem(key);
            clearedCount++;
          }
        } catch {
          // Remove corrupted entries
          localStorage.removeItem(key);
          clearedCount++;
        }
      }
    });
    
    console.info(`Cleaned up ${clearedCount} expired cache entries`);
  }
}
